/**
 * J3 — Bilan d'étape du profil politique aux tours 20, 32, 44, 56.
 *
 * Le profil cristallisé à T8 fige une signature initiale, mais il dérive
 * ensuite à chaque décision sans que le joueur ne le revoie. Ce composant
 * réaffiche le profil tous les 12 tours en mettant en évidence la dérive
 * depuis T8 — drift visible par axe, et nouvelle classification d'archétype
 * si la silhouette politique a basculé.
 *
 * Cf. project_retention_doctrine, levier #4 (profil cristallisé) — version
 * vivante.
 */

import { useEffect, useState } from "react";
import type { PoliticalProfile } from "../engine/types";
import { useGame } from "../store/useGame";
import {
  classifyProfile,
  PROFILE_T8_SNAPSHOT_KEY,
  type Archetype,
} from "./ProfileCrystalOverlay";

const TRIGGER_TURNS = [20, 32, 44, 56];
const STORAGE_KEY_PREFIX = "mp.profileMidterm.";

interface AxisDrift {
  label: string;
  initial: number;
  current: number;
  delta: number;
}

function axisLabel(key: keyof PoliticalProfile, value: number): string {
  // Affiche le pôle dominant — plus parlant qu'un score décimal.
  const isPos = value > 0;
  switch (key) {
    case "economic":
      return isPos ? "libéral" : "interventionniste";
    case "societal":
      return isPos ? "conservateur" : "progressiste";
    case "international":
      return isPos ? "souverainiste" : "ouvert";
    case "authority":
      return isPos ? "autoritaire" : "libertaire";
  }
}

function loadT8Snapshot(): PoliticalProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_T8_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PoliticalProfile;
  } catch {
    return null;
  }
}

function buildDrifts(
  initial: PoliticalProfile,
  current: PoliticalProfile,
): AxisDrift[] {
  const axes: (keyof PoliticalProfile)[] = [
    "economic",
    "societal",
    "international",
    "authority",
  ];
  return axes.map((key) => {
    const init = initial[key];
    const cur = current[key];
    return {
      label: `${axisLabel(key, cur)} (${key})`,
      initial: init,
      current: cur,
      delta: cur - init,
    };
  });
}

export function ProfileMidterm() {
  const worldState = useGame((s) => s.worldState);
  const [shown, setShown] = useState(false);
  const [drifts, setDrifts] = useState<AxisDrift[]>([]);
  const [archetype, setArchetype] = useState<Archetype | null>(null);
  const [triggerTurn, setTriggerTurn] = useState<number | null>(null);

  useEffect(() => {
    if (!worldState) return;
    if (!TRIGGER_TURNS.includes(worldState.turn)) return;
    const seenKey = `${STORAGE_KEY_PREFIX}${worldState.turn}`;
    if (localStorage.getItem(seenKey) === "yes") return;

    const t8 = loadT8Snapshot();
    if (!t8) return; // pas de référence — l'overlay T8 n'a pas eu lieu (peu probable).

    const current = worldState.polity.profile;
    setDrifts(buildDrifts(t8, current));
    setArchetype(classifyProfile(current));
    setTriggerTurn(worldState.turn);
    setShown(true);
  }, [worldState]);

  const close = () => {
    if (triggerTurn !== null) {
      try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${triggerTurn}`, "yes");
      } catch {
        /* quota */
      }
    }
    setShown(false);
  };

  if (!shown || !archetype || triggerTurn === null) return null;

  return (
    <div className="profile-midterm-backdrop" onClick={close}>
      <div
        className="profile-midterm-card"
        role="dialog"
        aria-modal="true"
        aria-label="Bilan d'étape du profil politique"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="midterm-pretitle">
          BILAN D'ÉTAPE · MOIS {triggerTurn}
        </div>
        <h2 className="midterm-name">{archetype.name}</h2>
        <p className="midterm-quote">« {archetype.quote} »</p>

        <ol className="midterm-drifts">
          {drifts.map((d) => {
            const sign = d.delta > 0 ? "+" : "";
            const trend =
              Math.abs(d.delta) < 0.05 ? "flat" : d.delta > 0 ? "up" : "down";
            return (
              <li key={d.label} className="midterm-drift" data-trend={trend}>
                <span className="drift-axis">{d.label}</span>
                <span className="drift-value">{d.current.toFixed(2)}</span>
                {trend !== "flat" && (
                  <span className="drift-delta">
                    {sign}
                    {d.delta.toFixed(2)} depuis T8
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        <button type="button" className="midterm-close" onClick={close}>
          POURSUIVRE
        </button>
      </div>
    </div>
  );
}
